import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import {
  OidcConfig,
  OidcProvider,
  ScopeProvider,
  defaultPermissionDefinitions,
} from '@nohara-dxdev/react-oidc';
import { CoreProvider, I18nConfig, LoadingScreen, queryConfig } from '@nohara-dxdev/react-core';

import enLocale from '@/locales/en.json';
import jaLocale from '@/locales/ja.json';
import { MainErrorFallback } from '@/components/errors/main';
import { Toaster } from '@/components/ui/sonner';
import { env } from '@/config/env';

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          ...queryConfig,
          queries: {
            ...queryConfig.queries,
            // Always treat BE responses as stale so navigation/remount refetches
            // fresh data instead of serving a cached response.
            staleTime: 0,
            gcTime: 0,
            refetchOnMount: 'always',
            // Bounded retry for reads: 3 attempts total against a 10s per-request
            // timeout, so a flaky response can't turn into an unbounded request
            // storm. Mutations are left un-retried on purpose — POST/PATCH here
            // are not idempotent, so a retry could double-apply a write.
            retry: 3,
          },
        },
      })
  );

  const [oidcConfig] = React.useState<OidcConfig>(() => ({
    apiEndpoint: env.HOME_API_ENDPOINT,
    oauthEndpoint: env.OAUTH_DOMAIN,
    userPoolId: env.USER_POOL_ID,
    userPoolWebClientId: env.USER_POOL_WEB_CLIENT_ID,
    oidcCallbackPath: env.OIDC_CALLBACK_PATH,
    signOutPath: env.SIGN_OUT_PATH,
    permissionDefinitions: defaultPermissionDefinitions,
  }));

  const i18nConfig = React.useMemo(
    (): I18nConfig => ({
      resources: {
        en: {
          translation: enLocale,
        },
        ja: {
          translation: jaLocale,
        },
      },
      defaultNS: 'translation',
      fallbackNS: 'translation',
      fallbackLng: 'en',
      lng: 'ja',
    }),
    []
  );

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <CoreProvider i18nConfig={i18nConfig}>
        <ErrorBoundary FallbackComponent={MainErrorFallback}>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV && <ReactQueryDevtools />}
            <OidcProvider config={oidcConfig}>
              <ScopeProvider>{children}</ScopeProvider>
            </OidcProvider>
            <Toaster />
          </QueryClientProvider>
        </ErrorBoundary>
      </CoreProvider>
    </React.Suspense>
  );
};
